import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Box, Button, Divider, List, Paper, Popper, TextField, Typography } from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { NoResults } from "lib/@components/no-results/no-results"
import { setReference } from "lib/@hooks/use-reference"
import { isEnabled } from "lib/@components/feature"
import noop from "lib/noop"
import { useScheduleEditorDraftTokens } from "./hooks/use-schedule-editor-draft-tokens"
import { useScheduleEditorMacroHandlers } from "./hooks/use-schedule-editor-macro-handlers"
import { useMacroAutocomplete } from "authoring/routes/macro/hooks/use-macro-autocomplete"
import { buildChipHtmlForToken } from "authoring/routes/macro/lib/macro-chip-utils"
import { MacroGuidanceOpen } from "authoring/routes/macro/macro-events"
import { FEATURE_FLAG_COMPONENT_MACROS } from "authoring/routes/macro/feature-flag-component-macros"
import { useMaintenanceTaskElementSelectorDialog } from "routes/component-library/reference-selector/use-maintenance-task-element-selector-dialog"
import Iconify from "minimals-template/components/Iconify"
import { MacroAutocompleteItem } from "./macro-autocomplete-item"
import { MacroGuidanceDetail } from "authoring/routes/macro/components/macro-guidance-detail"
import { resolveEditableTarget, resolveEditableInput } from "authoring/routes/macro/lib/macro-editable-target"
import { resolveSelectionRect } from "authoring/routes/macro/lib/macro-caret-utils"
import { buildSelectionSnapshot } from "authoring/routes/macro/lib/macro-selection-snapshot"
import {
    isNavigationKey,
    isSideNavigationKey,
    buildMacroToken,
    withTrailingSpace,
} from "authoring/routes/macro/lib/macro-token-helpers"
import { findContainingChip, findAdjacentMacroChip, removeMacroChip } from "authoring/routes/macro/lib/macro-chip-dom"
import {
    insertTokenIntoInput,
    insertTokenIntoContentEditable,
    insertChipIntoContentEditable,
    replacePartialCmpToken,
} from "authoring/routes/macro/lib/macro-token-insertion"

export function ScheduleMacroAutocomplete({ macros = [], enabled = true, isPreviewMode = false }) {
    const { macroMap } = useScheduleEditorDraftTokens()
    const { onClearComponentTokenTarget, onComponentTokenInserted } = useScheduleEditorMacroHandlers()
    const [anchorRect, setAnchorRect] = useState(null)
    const searchRef = useRef(null)
    const selectionRef = useRef(null)
    const selectionSnapshotRef = useRef(null)
    const activeElementRef = useRef(null)
    const suppressEventRef = useRef(false)
    const cmpDismissedRef = useRef(false)
    const [previewMacro, setPreviewMacro] = useState(null)
    const showComponentBrowse = isEnabled(FEATURE_FLAG_COMPONENT_MACROS)
    const openElementSelector = useMaintenanceTaskElementSelectorDialog({
        title: "Insert Component Token",
        applyCaption: "Insert",
        excludedValueTypes: ["json"],
    })

    const updateAnchor = useCallback(() => {
        const rect = resolveSelectionRect()
        if (rect) {
            setAnchorRect(rect)
            return
        }
        const fallback = activeElementRef.current?.getBoundingClientRect?.()
        if (fallback) {
            setAnchorRect(fallback)
        }
    }, [])

    const captureSelection = useCallback(() => {
        const selection = window.getSelection()
        if (!selection || selection.rangeCount === 0) return
        const range = selection.getRangeAt(0).cloneRange()
        selectionRef.current = range
        selectionSnapshotRef.current = buildSelectionSnapshot(activeElementRef.current, range)
        updateAnchor()
    }, [updateAnchor])

    const restoreSelection = useCallback(() => {
        const range = selectionRef.current
        if (!range) return false
        const selection = window.getSelection()
        if (!selection) return false
        selection.removeAllRanges()
        selection.addRange(range)
        if (activeElementRef.current?.focus) {
            activeElementRef.current.focus()
        }
        return true
    }, [])

    const handleInsertToken = useCallback(
        ({ macro, context, cursorIndex }) => {
            if (!enabled) return false
            const editable = resolveEditableTarget(activeElementRef.current)
            if (!editable) return false

            const insertToken = withTrailingSpace(buildMacroToken(macro))
            if (!insertToken) return false

            suppressEventRef.current = true
            restoreSelection()
            if (editable.type === "input") {
                return insertTokenIntoInput(editable.element, insertToken, context, cursorIndex)
            }
            if (editable.type === "contenteditable") {
                const chipHtml = buildChipHtmlForToken(macro.id, macroMap, macro) + " "
                return (
                    insertChipIntoContentEditable(editable.element, chipHtml, selectionSnapshotRef.current) ||
                    insertTokenIntoContentEditable(editable.element, insertToken, selectionSnapshotRef.current)
                )
            }
            return false
        },
        [enabled, macroMap, restoreSelection]
    )

    const {
        open,
        query,
        setQuery,
        results,
        activeIndex,
        setActiveIndex,
        handleInput,
        handleKeyDown: handleBubbleKeyDown,
        selectMacro,
        close,
    } = useMacroAutocomplete({
        macros,
        onInsert: handleInsertToken,
    })

    const anchorEl = useMemo(() => buildVirtualAnchor(anchorRect), [anchorRect])

    const handleEditableEvent = useCallback(
        (event) => {
            if (!enabled) return
            if (event?.type === "keyup" && event?.key === "Escape") return
            const target = event?.target
            if (isInBubble(target)) return
            if (event?.type === "keyup" && isNavigationKey(event.key) && open) return
            if (suppressEventRef.current) {
                suppressEventRef.current = false
                return
            }

            const editable = resolveEditableTarget(target)
            if (!editable) {
                onClearComponentTokenTarget?.()
                if (open) close()
                return
            }

            activeElementRef.current = editable.element
            if (!target?.closest?.(".task-row")) {
                onClearComponentTokenTarget?.()
            }
            const { value, cursorIndex } = resolveEditableInput(editable)
            handleInput({ value, cursorIndex })
            updateAnchor()
        },
        [close, enabled, handleInput, onClearComponentTokenTarget, open, updateAnchor]
    )

    useEffect(() => {
        if (!enabled) return noop
        const handleKeyUp = (event) => handleEditableEvent(event)
        const handleClick = (event) => handleEditableEvent(event)
        const handleInputEvent = (event) => handleEditableEvent(event)
        document.addEventListener("keyup", handleKeyUp, true)
        document.addEventListener("click", handleClick, true)
        document.addEventListener("input", handleInputEvent, true)
        return () => {
            document.removeEventListener("keyup", handleKeyUp, true)
            document.removeEventListener("click", handleClick, true)
            document.removeEventListener("input", handleInputEvent, true)
        }
    }, [enabled, handleEditableEvent])

    useEffect(bindBubbleKeyNavigation, [close, enabled, handleBubbleKeyDown, open])
    useEffect(bindMacroChipDeletionGuard, [enabled])
    useEffect(bindChipFocusIndicator, [enabled])

    useEffect(openComponentDialogOnCmpTrigger, [query]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!open) return
        captureSelection()
        if (searchRef.current) {
            searchRef.current.focus()
            searchRef.current.select()
        }
    }, [captureSelection, open])

    if (!enabled) return null

    return (
        <Popper
            open={open}
            anchorEl={anchorEl}
            placement="bottom-start"
            modifiers={[
                { name: "flip", enabled: true, options: { fallbackPlacements: ["top-start", "bottom-start"] } },
                { name: "preventOverflow", enabled: true, options: { boundary: "viewport", padding: 8 } },
            ]}
            sx={{ zIndex: 1400 }}
        >
            <Paper
                data-macro-autocomplete
                data-cy="schedule-macro-autocomplete"
                sx={{
                    width: 320,
                    p: 1,
                    boxShadow: 4,
                    borderRadius: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                }}
            >
                <If truthy={!!previewMacro && isPreviewMode}>
                    <MacroGuidanceDetail selectedMacro={previewMacro} onBack={handleClosePreview} maxHeight={400} />
                    <Else>
                        <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
                            Insert macro
                        </Typography>
                        <If value={showComponentBrowse}>
                            <Button
                                data-cy="schedule-macro-browse-components"
                                size="small"
                                fullWidth
                                startIcon={<Iconify icon="mdi:puzzle-outline" />}
                                onClick={handleBrowseComponents}
                                sx={{ mt: 0.5, textTransform: "none", justifyContent: "flex-start" }}
                            >
                                Browse components...
                            </Button>
                            <Divider sx={{ mt: 0.5 }} />
                        </If>
                        <Box sx={{ mt: 0.5 }}>
                            <TextField
                                size="small"
                                fullWidth
                                inputRef={searchRef}
                                value={query}
                                placeholder="Search macros"
                                inputProps={{ "data-cy": "schedule-macro-search" }}
                                onChange={handleQueryChange}
                                onKeyDown={handleSearchKeyDown}
                            />
                        </Box>
                        <Box sx={{ mt: 1, maxHeight: 260, overflowY: "auto" }}>
                            <If truthy={results.length > 0}>
                                <List dense sx={{ py: 0 }}>
                                    <Repeat
                                        list={results}
                                        keyFn="id"
                                        pass="macro"
                                        item={
                                            <MacroAutocompleteItem
                                                activeIndex={activeIndex}
                                                onSelect={handleSelectMacro}
                                                onHover={setActiveIndex}
                                                onView={handleViewMacro}
                                            />
                                        }
                                    />
                                </List>
                                <Else>
                                    <NoResults mt={0}>No macros match your search.</NoResults>
                                </Else>
                            </If>
                        </Box>
                    </Else>
                </If>
            </Paper>
        </Popper>
    )

    function handleClosePreview() {
        setPreviewMacro(null)
    }

    function handleQueryChange(event) {
        setQuery(event.target.value)
        setActiveIndex(0)
    }

    function handleSelectMacro(macro) {
        selectMacro(macro)
    }

    function handleSearchKeyDown(event) {
        if (event?.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            event.preventDefault()
            event.stopPropagation()
            handleViewMacro(results[activeIndex])
            return
        }
        if (event?.key === "Enter") {
            event.preventDefault()
            event.stopPropagation()
        }
        handleBubbleKeyDown(event)
    }

    function handleViewMacro(macro) {
        if (!macro?.id) return
        if (isPreviewMode) {
            setPreviewMacro(macro)
            return
        }
        setReference({ ScheduleRightBarTab: "AuthoringMacroGuidance" }, "tab-ScheduleRightBar-")
        requestAnimationFrame(() => {
            MacroGuidanceOpen.raiseOnce(macro.id)
            searchRef.current?.focus()
        })
    }

    function openComponentDialogOnCmpTrigger() {
        if (query !== "cmp:") {
            cmpDismissedRef.current = false
            return
        }
        if (!showComponentBrowse) return
        if (cmpDismissedRef.current) return
        handleBrowseComponents()
    }

    async function handleBrowseComponents() {
        // Capture snapshot before close() which may trigger state changes
        const snapshot = selectionSnapshotRef.current
        const storedElement = activeElementRef.current
        close()
        const reference = await openElementSelector()
        restoreSelection()
        if (!reference?.id) {
            cmpDismissedRef.current = true
            return
        }
        const tokenId = `cmp:${reference.id}`
        const token = `$${tokenId}$ `
        suppressEventRef.current = true
        const editable = resolveEditableTarget(storedElement ?? activeElementRef.current)
        if (!editable) return
        if (editable.type === "input") {
            insertTokenIntoInput(editable.element, token)
        } else if (editable.type === "contenteditable") {
            const chipHtml = buildChipHtmlForToken(tokenId, macroMap, { label: reference.label }) + " "
            if (
                !insertChipIntoContentEditable(editable.element, chipHtml, snapshot) &&
                !insertTokenIntoContentEditable(editable.element, token, snapshot)
            ) {
                // Selection was stale (e.g. Jodit replaced DOM nodes while dialog was open).
                // Fall back to searching the contenteditable text for the partial $cmp: token.
                replacePartialCmpToken(editable.element, chipHtml, token)
            }
        }

        // Notify the task context that a component token was inserted so it can
        // auto-wire the componentRef for property inheritance.
        // Strip any ::element suffix to get the base task ID.
        const baseTaskId = reference.id.split("::")[0]
        onComponentTokenInserted?.(baseTaskId)
    }

    function bindBubbleKeyNavigation() {
        if (!enabled) return noop
        const handleKeyDown = (event) => {
            if (!open) return
            const inBubble = isInBubble(event.target)
            if (event.metaKey || event.ctrlKey || event.altKey) return
            if (isSideNavigationKey(event.key)) {
                if (!inBubble) {
                    suppressEventRef.current = true
                    close()
                }
                return
            }
            if (!isNavigationKey(event.key)) return
            event.preventDefault()
            event.stopImmediatePropagation()
            handleBubbleKeyDown(event)
        }
        const handlePendingKeyDown = (event) => {
            if (open) return
            if (isInBubble(event.target)) return
            if (event.metaKey || event.ctrlKey || event.altKey) return
            if (!isNavigationKey(event.key)) return
            close()
        }
        document.addEventListener("keydown", handleKeyDown, true)
        document.addEventListener("keydown", handlePendingKeyDown, true)
        return () => {
            document.removeEventListener("keydown", handleKeyDown, true)
            document.removeEventListener("keydown", handlePendingKeyDown, true)
        }
    }

    function bindMacroChipDeletionGuard() {
        if (!enabled) return noop

        document.addEventListener("keydown", handleKeyDown, true)
        return () => document.removeEventListener("keydown", handleKeyDown, true)

        function handleKeyDown(event) {
            if (event.defaultPrevented) return
            if (event.metaKey || event.ctrlKey || event.altKey) return
            if (event.key !== "Backspace" && event.key !== "Delete" && event.key !== "Enter") return
            if (isInBubble(event.target)) return

            const editable = resolveEditableTarget(event.target)
            if (!editable || editable.type !== "contenteditable") return

            const selection = window.getSelection()
            if (!selection || selection.rangeCount === 0) return

            const range = selection.getRangeAt(0)
            if (!range.collapsed || !editable.element.contains(range.startContainer)) return

            // Jodit's paragraph-merge can programmatically place the cursor inside a
            // contenteditable="false" chip (e.g. after Enter → Backspace). Handle both
            // deletion and Enter while the cursor is stranded inside a chip.
            const containingChip = findContainingChip(range.startContainer, editable.element)
            if (containingChip) {
                if (event.key === "Enter") {
                    // Move cursor to just after the chip before Jodit processes the Enter.
                    // This prevents the browser from splitting the paragraph inside the chip
                    // and cloning the chip shell into every subsequent new line.
                    const afterRange = document.createRange()
                    afterRange.setStartAfter(containingChip)
                    afterRange.collapse(true)
                    selection.removeAllRanges()
                    selection.addRange(afterRange)
                    return // let the Enter event continue from outside the chip
                }
                event.preventDefault()
                event.stopPropagation()
                const direction = event.key === "Backspace" ? "backward" : "forward"
                removeMacroChip(containingChip, direction)
                editable.element.dispatchEvent(new Event("input", { bubbles: true }))
                return
            }

            if (event.key === "Enter") return

            const direction = event.key === "Backspace" ? "backward" : "forward"
            const chip = findAdjacentMacroChip(range, direction)
            if (!chip) return

            event.preventDefault()
            event.stopPropagation()
            removeMacroChip(chip, direction)
            editable.element.dispatchEvent(new Event("input", { bubbles: true }))
        }
    }

    function bindChipFocusIndicator() {
        if (!enabled) return noop
        let focusedChip = null

        document.addEventListener("selectionchange", handleSelectionChange)
        return () => {
            document.removeEventListener("selectionchange", handleSelectionChange)
            clearChipFocus()
        }

        function handleSelectionChange() {
            const selection = window.getSelection()
            if (!selection || selection.rangeCount === 0) {
                clearChipFocus()
                return
            }
            const range = selection.getRangeAt(0)
            const node = range.startContainer
            const element = node.nodeType === Node.TEXT_NODE ? node.parentElement : node
            const editable = resolveEditableTarget(element)
            const chip = editable ? findContainingChip(node, editable.element) : null

            if (chip === focusedChip) return
            clearChipFocus()
            if (chip) {
                chip.dataset.unfocusedBorder = chip.style.border
                chip.style.border = "1px solid var(--mui-palette-info-dark, #1165B3)"
                focusedChip = chip
            }
        }

        function clearChipFocus() {
            if (!focusedChip) return
            focusedChip.style.border = focusedChip.dataset.unfocusedBorder ?? ""
            delete focusedChip.dataset.unfocusedBorder
            focusedChip = null
        }
    }
}
function buildVirtualAnchor(rect) {
    if (!rect) return null
    return {
        getBoundingClientRect: () => rect,
    }
}

function isInBubble(target) {
    if (!target || typeof target.closest !== "function") return false
    return !!target.closest("[data-macro-autocomplete]")
}
